import useSWR from "swr";
import { Download, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/Common";
import { CopyButton } from "@/components/KeyDialog";
import { API_BASE, fetcher } from "@/lib/api";
import { fmtBytes } from "@/lib/format";

const SQL = `CREATE USER 'sentinel_ro'@'localhost' IDENTIFIED BY 'choose-a-long-password';
GRANT SELECT, SHOW VIEW ON *.* TO 'sentinel_ro'@'localhost';
FLUSH PRIVILEGES;`;

const VERIFY = `Get-Service CuvanoVIDSEdge | Select-Object Name, Status, StartType
sc.exe qc CuvanoVIDSEdge
sc.exe qfailure CuvanoVIDSEdge`;

const STEPS = [
  { title: "Register the server", text: "In Connections, click Register server and keep the device key window open. The key is shown only once." },
  { title: "Create a read-only MySQL user", text: "On the Windows server, run this in MySQL Workbench or the mysql command line as root, and replace the password. The user can only read. For tighter security, grant per database instead (GRANT SELECT, SHOW VIEW ON yourdb.* …) so it can't read MySQL's own system tables. New databases then need their own grant.", code: SQL, copyId: "copy-mysql-sql-btn" },
  { title: "Copy CuvanoEdge.exe to the server", text: "Download the package below, unzip it on the server (e.g. to the Desktop) and double-click CuvanoEdge.exe. Approve the Windows administrator prompt. If SmartScreen warns, choose More info → Run anyway (the exe is not code-signed yet)." },
  { title: "Fill in Settings and test", text: "On the Settings tab enter MySQL host 127.0.0.1, port 3306, the read-only user and password, then paste the dashboard address and device key. Click Test MySQL connection and Test dashboard connection, then Save." },
  { title: "Install and start the service", text: "On the Status tab click Install service, then Start. The service copies itself to C:\\Program Files\\Cuvano VIDS, runs as NT SERVICE\\CuvanoVIDSEdge, starts automatically after boot without anyone logging in, and restarts itself after 1 minute if it ever stops." },
  { title: "Watch the first scan and export", text: "The Export progress tab shows each table, rows sent, speed and time left. Here, the server turns Online and tables appear in the Data explorer as they arrive." },
  { title: "Confirm it survives a reboot", text: "Restart the server once. Without logging in, wait 3–5 minutes and check the heartbeat here. You can also check from PowerShell:", code: VERIFY, copyId: "copy-powershell-btn" },
];

function Step({ n, s }) {
  return (
    <li className="flex gap-4" data-testid={`setup-step-${n}`}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">{n}</div>
      <div className="min-w-0 flex-1 pb-8">
        <div className="font-medium">{s.title}</div>
        <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
        {s.code && (
          <div className="mt-3 rounded-2xl bg-secondary p-4">
            <pre className="overflow-x-auto font-mono text-xs leading-relaxed">{s.code}</pre>
            <div className="mt-3"><CopyButton value={s.code} testId={s.copyId} /></div>
          </div>
        )}
      </div>
    </li>
  );
}

export default function Setup() {
  const { data: info } = useSWR("/downloads/info", fetcher);
  return (
    <div>
      <PageHeader title="Setup guide" subtitle="Install the Edge Sender on the Windows server. It pushes data out over HTTPS, so nothing changes on your firewall or router." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <ol className="surface p-6">{STEPS.map((s, i) => <Step key={i} n={i + 1} s={s} />)}</ol>
        <div className="space-y-4">
          <div className="surface p-5" data-testid="edge-download-card">
            <div className="eyebrow">Edge Sender for Windows</div>
            <div className="mt-2 text-lg font-semibold">Cuvano VIDS Edge Sender {info?.version || ""}</div>
            <p className="mt-1 text-xs text-muted-foreground">Windows Server 2016–2025 or Windows 10/11, 64-bit. One file, nothing else to install.</p>
            {info?.available ? (
              <>
                <Button asChild className="mt-4 w-full rounded-full" data-testid="download-edge-sender-button">
                  <a href={`${API_BASE}/downloads/edge-sender`}><Download className="mr-1.5 h-4 w-4" /> Download ({fmtBytes(info.size_bytes)})</a>
                </Button>
                <div className="mt-3 break-all font-mono text-[10px] text-muted-foreground" data-testid="edge-package-sha256">SHA-256 {info.sha256}</div>
              </>
            ) : <p className="mt-4 text-sm text-muted-foreground" data-testid="edge-package-unavailable">The package is not available yet.</p>}
          </div>
          <div className="surface p-5">
            <div className="mb-3 flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4 text-[hsl(var(--ok))]" /> Security at a glance</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Outbound HTTPS (port 443) only. No inbound ports, no port forwarding.</li>
              <li>Reads MySQL locally with a read-only user. Never writes to it.</li>
              <li>Every upload is signed with the device key and time-stamped; tampered or replayed uploads are refused.</li>
              <li>MySQL password and device key are encrypted on the server with Windows DPAPI.</li>
              <li>Control panel talks to the service over a local pipe only administrators can open.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
